// src/pages/ExamDetailPage.jsx - REDESIGNED (Black & White Theme)
import React, { useState, useEffect, useMemo } from 'react';
import { Book, Target, Award, Clock, ChevronRight, History, ArrowLeft, Zap, Lock, ArrowUpRight, Home, BookOpen, GraduationCap, Monitor } from 'lucide-react';
import examService from '../../services/examService.js';
import enhancedUserService from '../../services/enhancedUserService.js';
import subscriptionService from '../../services/subscriptionService.js';
import freeAttemptService from '../../services/freeAttemptService.js';
import { trackPaywallShown } from '../../utils/conversionEvents.js';
import { auth } from '../../firebase.js';
import { onAuthStateChanged } from 'firebase/auth';
import { Navigate } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import examAnalyticsService from '../../services/examAnalyticsService.js';
import { useTranslation } from 'react-i18next';
import NoExamSelected from './NoExamSelected.jsx';
import GrammarBottomNav from '../grammar/GrammarBottomNav.jsx';
import { getGoetheModuleSummary } from '../../utils/goetheExamDisplay.js';

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';

const ExamDetailPage = ({ examData, onStartMockExam, onBack, seoH1 }) => {
  // Safety guard: Show message if no exam is selected
  if (!examData || !examData.examProvider || !examData.level) {
    return <NoExamSelected onBack={onBack} />;
  }
  const { t, i18n } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [attemptHistory, setAttemptHistory] = useState([]);
  const [isLoadingAttempt, setIsLoadingAttempt] = useState(true);
  const [availableVariants, setAvailableVariants] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [overlayReason, setOverlayReason] = useState('locked'); // 'locked' | 'attempt-used'
  const [freeAttempts, setFreeAttempts] = useState(null);

  // auth.currentUser is NOT reactive: on a cold load this page can mount before
  // Firebase restores the session, and nothing re-renders when it arrives. Read
  // directly, it left subscriptionStatus defaulted to free (locking a PAYING
  // user out of their variants) and freeAttempts stuck at null (hiding the free
  // user's "attempt used" overlay). Track the uid in state so both effects re-run.
  const [authUid, setAuthUid] = useState(() => auth.currentUser?.uid ?? null);
  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUid(u?.uid ?? null)), []);

  useEffect(() => {
    if (!authUid) { setFreeAttempts(null); return; }
    freeAttemptService.getFreeAttempts(authUid).then(setFreeAttempts);
  }, [authUid]);

  // Hover states for new button style
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [isStartHovered, setIsStartHovered] = useState({}); // For multiple start buttons

const [analyticsData, setAnalyticsData] = useState(null);
const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  useEffect(() => {
    if (!examData?.examId || !authUid) {
      setIsLoadingAttempt(false);
      return;
    }

    setIsLoadingAttempt(true);
    const unsubscribe = enhancedUserService.subscribeExamResultsHistory(
      authUid,
      examData.examId,
      (history) => {
        setAttemptHistory(history);
        setIsLoadingAttempt(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [examData?.examId, authUid]);

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      // Wait for a real uid before deciding. Defaulting to free while auth is
      // still restoring would lock a premium user's variants on every cold load.
      if (!authUid) return;
      try {
        const status = await subscriptionService.getSubscriptionStatus(authUid);
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error loading subscription status:', error);
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      }
    };

    const loadVariants = async () => {
      if (!examData?.examProvider || !examData?.level) {
        setIsLoadingVariants(false);
        return;
      }
      setIsLoadingVariants(true);
      try {
        const allExams = examService.getAvailableExams();
        const providerKey = examData.examProvider.toLowerCase();
        const levelKey = examData.level.toUpperCase();
        const allVariants = allExams[providerKey]?.[levelKey] || [];
        setAvailableVariants(allVariants);
      } catch (error) {
        console.error("Could not fetch exam variants:", error);
        setAvailableVariants([]);
      } finally {
        setIsLoadingVariants(false);
      }
    };

    loadSubscriptionStatus();
    loadVariants();
  }, [examData, authUid]);
  useEffect(() => {
    if (attemptHistory.length > 0) {
      setIsLoadingAnalytics(true);
      try {
        // Import examAnalyticsService at the top first!
        const overallMetrics = examAnalyticsService.calculateOverallMetrics(attemptHistory);
        const sectionMetrics = examAnalyticsService.calculateSectionMetrics(attemptHistory);
        const radarData = examAnalyticsService.prepareRadarChartData(
          sectionMetrics,
          attemptHistory[attemptHistory.length - 1] // latest exam
        );
        const strengthsWeaknesses = examAnalyticsService.identifyStrengthsWeaknesses(sectionMetrics);

        setAnalyticsData({
          history: attemptHistory,
          overall: overallMetrics,
          sections: sectionMetrics,
          radar: radarData,
          strengths: strengthsWeaknesses.strengths,
          weaknesses: strengthsWeaknesses.weaknesses
        });
      } catch (error) {
        console.error('Error calculating analytics:', error);
      } finally {
        setIsLoadingAnalytics(false);
      }
    }
  }, [attemptHistory]);
  const handleViewAttempt = (attempt) => {
    navigate('/exam/results', { state: { results: attempt, examData: examData } });
  };

  // Old exam results carry the pre-rename variant display name (e.g.
  // 'Übungstest Zwei') — map it to the current city name for the history list.
  const legacyVariantNameMap = useMemo(() => {
    const map = {};
    const providerKey = examData?.examProvider?.toLowerCase();
    const levelKey = examData?.level?.toUpperCase();
    const variants = examService.getAvailableExams()[providerKey]?.[levelKey] || [];
    variants.forEach((v) => { if (v.legacyName && v.legacyName !== v.name) map[v.legacyName] = v.name; });
    return map;
  }, [examData?.examProvider, examData?.level]);

  if (!examData) {
    return <Navigate to="/dashboard" />;
  }

  const overview = examService.getExamOverview(examData);

  if (!overview) {
     return <Navigate to="/dashboard" />;
  }

  const handleStartVariant = (variantFile) => {
    // Free plan: the one free exam attempt is already used up.
    if (subscriptionStatus?.isFree && freeAttemptService.isExamUsed(freeAttempts)) {
      setOverlayReason('attempt-used');
      setShowPremiumOverlay(true);
      trackPaywallShown({ context: 'exam_attempt_used' });
      return;
    }
    onStartMockExam(variantFile);
  };

  // Handlers for start button hover states
  const handleStartMouseEnter = (id) => {
    setIsStartHovered(prev => ({ ...prev, [id]: true }));
  };
  const handleStartMouseLeave = (id) => {
    setIsStartHovered(prev => ({ ...prev, [id]: false }));
  };


  return (
    <div className="bg-white min-h-screen relative overflow-hidden exam-detail-page pb-24 sm:pb-0">
      {/* Premium Overlay - Shows for free users when clicking locked exams */}
      {showPremiumOverlay && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fade-in">
          <div className={`${ELEVATED_SURFACE} max-w-sm w-full p-6 sm:p-8 animate-slide-up`}>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
                  <Lock className="w-7 h-7 text-gray-900" />
                </div>
              </div>

              <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
                {overlayReason === 'attempt-used'
                  ? t('premium.required.mockExam.attemptUsedTitle', { defaultValue: 'Your free attempt is used' })
                  : t('premium.required.mockExam.title', { defaultValue: 'Do not risk exam day' })}
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                {overlayReason === 'attempt-used'
                  ? t('premium.required.mockExam.attemptUsedBody', {
                      defaultValue: 'The free plan includes one full mock exam attempt. Upgrade to Premium for unlimited {{provider}} {{level}} mock exams and variants.',
                      provider: (examData?.examProvider || 'TELC').toUpperCase(),
                      level: (examData?.level || 'B1').toUpperCase(),
                    })
                  : t('premium.required.mockExam.body', {
                      defaultValue: 'Practice complete {{provider}} {{level}} mock exams first. Go into the real exam prepared.',
                      provider: (examData?.examProvider || 'TELC').toUpperCase(),
                      level: (examData?.level || 'B1').toUpperCase(),
                    })}
              </p>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-6">
                {t('premium.required.mockExam.subtext', { defaultValue: 'Timed variants + score prediction' })}
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setShowPremiumOverlay(false);
                  }}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-black via-gray-900 to-gray-700 text-white font-bold text-sm hover:shadow-[0_8px_20px_rgba(17,24,39,0.28)] active:scale-[0.98] touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  {t('premium.required.mockExam.cta', { defaultValue: 'Unlock mock exams' })}
                </button>

                <button
                  onClick={() => setShowPremiumOverlay(false)}
                  className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                >
                  {t('premium.required.mockExam.secondary', { defaultValue: 'Maybe later' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Header ──────────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8 pt-4 pb-6">
        <div className="max-w-7xl mx-auto">

          {/* Top row: Back (left) + Logo (right), both compact */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              onMouseEnter={() => setIsBackHovered(true)}
              onMouseLeave={() => setIsBackHovered(false)}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-md flex-shrink-0 touch-manipulation"
            >
              <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: isBackHovered ? '100%' : '0%', background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)', transition: 'width 0.7s ease-out', zIndex: 0 }} />
              <ArrowLeft className="w-4 h-4 relative z-10 transition-colors duration-500" style={{ color: isBackHovered ? '#ffffff' : '#000000' }} />
              <span className="text-sm font-semibold relative z-10 transition-colors duration-500" style={{ color: isBackHovered ? '#ffffff' : '#000000' }}>{t('common.goBack')}</span>
            </button>
          </div>

          {/* Title + subtitle (left) | Stats strip (right), same row */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="font-space text-2xl sm:text-3xl lg:text-4xl font-black text-black tracking-tight leading-tight mb-1">
                {seoH1 || overview.examName}
              </h1>
              <p className="text-sm text-black/50">{t('exam.summaryDescription')}</p>
            </div>

            {/* Stats, compact inline strip (21st.dev StatsSection divide-x pattern) */}
            <div className="inline-flex items-stretch rounded-xl border border-black/10 bg-white overflow-hidden divide-x divide-black/10 shadow-sm flex-shrink-0">
              <div className="flex flex-col items-center justify-center px-4 py-2.5 gap-0.5">
                <Clock className="w-3.5 h-3.5 text-black/30 mb-1" />
                <span className="font-space text-base font-black text-black leading-none">{overview.duration}</span>
                <span className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">{t('exam.minutesLabel')}</span>
              </div>
              <div className="flex flex-col items-center justify-center px-4 py-2.5 gap-0.5">
                <Book className="w-3.5 h-3.5 text-black/30 mb-1" />
                <span className="font-space text-base font-black text-black leading-none">{overview.totalQuestions}</span>
                <span className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">{t('exam.questionsLabel')}</span>
              </div>
              <div className="flex flex-col items-center justify-center px-4 py-2.5 gap-0.5">
                <Target className="w-3.5 h-3.5 text-black/30 mb-1" />
                <span className="font-space text-base font-black text-black leading-none">{overview.passingScore}%</span>
                <span className="text-[9px] text-black/35 font-semibold uppercase tracking-wider">{t('exam.passLabel')}</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Content ─────────────────────────────────────────────── */}
      <section className="pt-6 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Desktop recommendation bar */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-red-800">
            <Monitor className="w-4 h-4 flex-shrink-0 text-red-600" aria-hidden />
            <p className="text-sm font-medium">
              {t('exam.desktopRecommendation', 'We highly recommend using the desktop version for the best exam experience.')}
            </p>
            </div>
          </div>

          {/* Section label */}
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white flex-shrink-0">
              <Award className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-space text-lg font-black text-black tracking-tight">{t('exam.mockVariantsTitle')}</h2>
            </div>
          </div>

          {/* Cards grid. Dark Grid pattern from 21st.dev */}
          {isLoadingVariants ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true" aria-label={t('common.loading', { defaultValue: 'Loading' })}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="relative bg-white rounded-2xl p-5 border-[3px] border-black/10 animate-pulse">
                  <div className="absolute top-3 right-3 w-12 h-8 bg-black/5 rounded" />
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-black/10 rounded-xl mb-3" />
                    <div className="h-4 w-24 bg-black/10 rounded mb-2" />
                    <div className="h-3 w-32 bg-black/5 rounded mb-5" />
                    <div className="h-10 w-full max-w-[120px] bg-black/10 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : availableVariants.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableVariants.map((variant, index) => {
                const isLocked = subscriptionStatus?.isFree && index > 0;
                const isComingSoon = variant.comingSoon === true;
                return (
                  <div
                    key={variant.id}
                    className={`exam-variant-card group relative rounded-2xl p-5 border-[3px] transition-all duration-500 ease-out ${isLocked ? 'exam-variant-card--premium border-black bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-[0_8px_24px_rgba(17,24,39,0.1)]' : 'border-black bg-white'} ${isComingSoon ? 'opacity-50 pointer-events-none select-none' : ''}`}
                  >
                    <div className="exam-shine-effect" />
                    <div className="exam-card-number">{String(index + 1).padStart(2, '0')}</div>
                    <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/20 transition-colors duration-500" />
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/20 transition-colors duration-500" />
                    {isLocked && !isComingSoon && (
                      <div className="absolute top-3 left-3 z-20 inline-flex items-center rounded-full border border-black/20 bg-black px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                        {t('premium.only')}
                      </div>
                    )}

                    <div className="relative text-center">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-3 mx-auto transition-all duration-500 group-hover:bg-white group-hover:text-[#3A86FF] group-hover:scale-110 group-hover:rotate-6"
                        style={{ backgroundColor: isLocked ? '#111827' : '#000000' }}
                      >
                        <Zap className="w-5 h-5" />
                      </div>
                      <h3 className="font-space font-black text-base text-black mb-1 break-words">{variant.name}</h3>
                      {variant.description ? (
                        <p className="text-xs text-black/60 mb-5 line-clamp-2">{variant.description}</p>
                      ) : (
                        <div className="mb-5" />
                      )}
                      {isComingSoon ? (
                        <button disabled className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 rounded-xl border-2 border-gray-300 cursor-not-allowed">
                          <span className="text-gray-500 text-sm font-medium">{t('comingSoon.badge')}</span>
                        </button>
                      ) : isLocked ? (
                        <button
                          onClick={() => { setOverlayReason('locked'); setShowPremiumOverlay(true); }}
                          onMouseEnter={() => handleStartMouseEnter(variant.id)}
                          onMouseLeave={() => handleStartMouseLeave(variant.id)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 rounded-xl border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl"
                        >
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: isStartHovered[variant.id] ? '100%' : '0%', background: 'linear-gradient(to right, #d1d5db, #6b7280, #111827)', transition: 'width 0.7s ease-out', zIndex: 0 }} />
                          <span className="font-space font-bold text-sm relative z-10 transition-colors duration-500" style={{ color: isStartHovered[variant.id] ? '#ffffff' : '#000000' }}>{t('button.start')}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStartVariant(variant.file)}
                          onMouseEnter={() => handleStartMouseEnter(variant.id)}
                          onMouseLeave={() => handleStartMouseLeave(variant.id)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white rounded-xl border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl"
                        >
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: isStartHovered[variant.id] ? '100%' : '0%', background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)', transition: 'width 0.7s ease-out', zIndex: 0 }} />
                          <span className="font-space font-bold text-sm relative z-10 transition-colors duration-500" style={{ color: isStartHovered[variant.id] ? '#ffffff' : '#000000' }}>{t('button.start')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="exam-variant-card group relative bg-white rounded-2xl p-5 border-[3px] border-black transition-all duration-500 ease-out max-w-xs">
              <div className="exam-shine-effect" />
              <div className="exam-card-number">01</div>
              <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/20 transition-colors duration-500" />
              <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/20 transition-colors duration-500" />
              <div className="relative text-center">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white mb-3 mx-auto transition-all duration-500 group-hover:bg-white group-hover:text-[#3A86FF] group-hover:scale-110 group-hover:rotate-6">
                  <Zap className="w-5 h-5" />
                </div>
                <h3 className="font-space font-black text-base text-black mb-1">{t('exam.defaultVariantName')}</h3>
                <p className="text-xs text-black/60 mb-5">{t('exam.defaultVariantDescription')}</p>
                <button
                  onClick={() => onStartMockExam()}
                  onMouseEnter={() => handleStartMouseEnter('default')}
                  onMouseLeave={() => handleStartMouseLeave('default')}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white rounded-xl border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl"
                >
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: isStartHovered['default'] ? '100%' : '0%', background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)', transition: 'width 0.7s ease-out', zIndex: 0 }} />
                  <span className="font-space font-bold text-sm relative z-10 transition-colors duration-500" style={{ color: isStartHovered['default'] ? '#ffffff' : '#000000' }}>{t('button.start')}</span>
                </button>
              </div>
            </div>
          )}

          <style>{`
            .exam-variant-card { position: relative; overflow: hidden; }
            .exam-variant-card:hover { transform: scale(1.03) translateY(-4px); box-shadow: 0 20px 40px rgba(0,0,0,0.25); background: linear-gradient(to bottom right, #000, #1a1a1a); }
            .exam-variant-card--premium:hover { background: linear-gradient(to bottom right, #111111, #2b2b2b); border-color: #000000; }
            .exam-card-number { position: absolute; top: 0.75rem; right: 1rem; font-family: 'Space Grotesk', sans-serif; font-size: 3.5rem; font-weight: 900; color: #3A86FF; opacity: 0.12; line-height: 1; transition: all 0.5s ease; z-index: 6; }
            @media (min-width: 640px) { .exam-card-number { font-size: 4.5rem; } }
            .exam-variant-card:hover .exam-card-number { opacity: 0.22; color: #ffffff; }
            .exam-variant-card--premium:hover .exam-card-number { color: #d1d5db; opacity: 0.24; }
            .exam-variant-card:hover h3 { color: #ffffff; }
            .exam-variant-card:hover p { color: rgba(255,255,255,0.6); }
            .exam-shine-effect { position: absolute; top: 0; left: -100%; width: 100%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent); transition: left 0.7s ease; z-index: 5; }
            .exam-variant-card:hover .exam-shine-effect { left: 100%; }
            .exam-variant-card .relative { position: relative; z-index: 10; }
          `}</style>
            {/* --- Exam History Section --- */}
            {!isLoadingAttempt && attemptHistory.length > 0 && (
              <div className="mt-12 border-t-2 border-gray-100 pt-10">
                  <h2 className="font-space text-3xl font-black text-black mb-8 tracking-tight flex items-center">
                    <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white mr-4">
                        <History className="w-6 h-6" />
                    </div>
                    {t('exam.historyTitle')}
                  </h2>

                  {isLoadingAttempt ? (
                      <p className="text-black/60">{t('exam.loadingHistory')}</p>
                  ) : (
                      <ul className="space-y-4">
                          {attemptHistory.map((attempt, index) => (
                              <li key={attempt.id}>
                                  <button
                                      onClick={() => handleViewAttempt(attempt)}
                                      className="w-full flex justify-between items-center p-4 sm:p-6 bg-white rounded-2xl border-2 border-black/15 hover:border-[#3A86FF] transition-all duration-300 text-left group"
                                  >
                                      <div className="flex items-center gap-4">
                                          <div className="hidden sm:block text-center p-3 rounded-lg bg-gray-100 group-hover:bg-[#3A86FF]/10 transition-colors">
                                              <p className="font-space font-black text-[#3A86FF] text-2xl">
    {(() => {
      const goethe = getGoetheModuleSummary(attempt);
      if (goethe) return `${goethe.displayShort}`;
      return `${Math.round(attempt.overallScore)}/${attempt.maxScore}`;
    })()}
</p>
                                              {getGoetheModuleSummary(attempt) && (
                                                <p className="text-xs text-black/60 mt-0.5">{t('exam.modulesLabel')}</p>
                                              )}
                                          </div>
                                          <div>
                                            <p className="font-space font-bold text-black text-lg">
                                              {legacyVariantNameMap[attempt.variantName] || attempt.variantName || t('exam.attemptLabel', { number: attemptHistory.length - index })}
                                            </p>
                                            <p className="text-sm text-black/60 mt-1">
                                                {attempt.completedAt?.toDate().toLocaleDateString(i18n.language || 'en-US', {
                                                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                })}
                                            </p>
                                          </div>
                                      </div>
                                      <div className="flex items-center text-[#3A86FF]">
                                          <span className="hidden sm:inline font-semibold mr-2 group-hover:underline">
                                              {t('exam.viewResults')}
                                          </span>
                                          <ChevronRight className="w-5 h-5 transform transition-transform group-hover:translate-x-1" />
                                      </div>
                                  </button>
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
            )}
        </div>
      </section>

      {/* Bottom nav: Home, Lessons, Practice (mobile only) */}
      <GrammarBottomNav
        buttons={[
          {
            id: 'home',
            icon: Home,
            label: t('nav.home', { defaultValue: 'Home' }),
            onClick: () => navigate('/dashboard'),
            color: '#3A86FF',
          },
          {
            id: 'lessons',
            icon: BookOpen,
            label: t('nav.lessons', { defaultValue: 'Lessons' }),
            onClick: () => navigate('/lessons'),
            color: '#3A86FF',
          },
          {
            id: 'practice',
            icon: GraduationCap,
            label: t('nav.practice', { defaultValue: 'Practice' }),
            onClick: () => navigate('/practice'),
            color: '#3A86FF',
          },
        ]}
      />
    </div>
  );
};

export default ExamDetailPage;